import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowRotateClockwise24Regular,
  ArrowRotateCounterclockwise24Regular,
  Crop24Regular,
} from '@fluentui/react-icons';
import {
  clampCrop,
  cropImageDataUrl,
  rotateImageDataUrl,
  type CropRect,
} from './photoTransform';

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const;
type Handle = (typeof HANDLES)[number];

type Drag =
  | { kind: 'move'; startX: number; startY: number; orig: CropRect }
  | { kind: Handle; startX: number; startY: number; orig: CropRect };

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minWidth: 0,
  },
  stage: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackgroundInverted,
    lineHeight: 0,
  },
  image: {
    display: 'block',
    width: '100%',
    height: 'auto',
    maxHeight: 'min(60vh, 480px)',
    objectFit: 'contain',
    userSelect: 'none',
    touchAction: 'none',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    touchAction: 'none',
    cursor: 'move',
  },
  crop: {
    position: 'absolute',
    boxSizing: 'border-box',
    border: `2px solid ${tokens.colorNeutralForegroundOnBrand}`,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
  },
  handle: {
    position: 'absolute',
    width: '20px',
    height: '20px',
    margin: '-10px 0 0 -10px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralForegroundOnBrand,
    border: `2px solid ${tokens.colorBrandBackground}`,
    boxSizing: 'border-box',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    '> *': {
      flexGrow: 0,
    },
  },
  busy: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
});

const handleCursor: Record<Handle, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

const handlePosition = (handle: Handle): { left: string; top: string } => ({
  left: handle.includes('w') ? '0%' : handle.includes('e') ? '100%' : '50%',
  top: handle.includes('n') ? '0%' : handle.includes('s') ? '100%' : '50%',
});

const applyDrag = (drag: Drag, x: number, y: number): CropRect => {
  const dx = x - drag.startX;
  const dy = y - drag.startY;
  const { orig } = drag;
  if (drag.kind === 'move') {
    return { ...orig, x: orig.x + dx, y: orig.y + dy };
  }
  let next = { ...orig };
  if (drag.kind.includes('e')) next = { ...next, width: orig.width + dx };
  if (drag.kind.includes('w')) {
    next = { ...next, x: orig.x + dx, width: orig.width - dx };
  }
  if (drag.kind.includes('s')) next = { ...next, height: orig.height + dy };
  if (drag.kind.includes('n')) {
    next = { ...next, y: orig.y + dy, height: orig.height - dy };
  }
  return next;
};

export type PhotoEditorProps = {
  src: string;
  onSrcChange: (src: string) => void;
  onEditingChange?: (editing: boolean) => void;
  alt?: string;
};

export const PhotoEditor = ({
  src,
  onSrcChange,
  onEditingChange,
  alt,
}: PhotoEditorProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const imgRef = useRef<HTMLImageElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const [cropping, setCropping] = useState(false);
  const [crop, setCrop] = useState<CropRect | null>(null);
  const [busy, setBusy] = useState(false);
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [viewSize, setViewSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    setCropping(false);
    setCrop(null);
  }, [src]);

  useEffect(() => {
    onEditingChange?.(cropping || busy);
  }, [busy, cropping, onEditingChange]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const sync = () =>
      setViewSize({ width: img.clientWidth, height: img.clientHeight });
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(img);
    return () => observer.disconnect();
  }, [src]);

  const toNatural = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      const img = imgRef.current;
      if (!img || !natural.width) return null;
      const rect = img.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      return {
        x: ((clientX - rect.left) / rect.width) * natural.width,
        y: ((clientY - rect.top) / rect.height) * natural.height,
      };
    },
    [natural]
  );

  const rotate = async (degrees: 90 | -90) => {
    if (busy || cropping) return;
    setBusy(true);
    try {
      onSrcChange(await rotateImageDataUrl(src, degrees));
    } finally {
      setBusy(false);
    }
  };

  const startCrop = () => {
    if (!natural.width) return;
    setCrop(
      clampCrop(
        {
          x: natural.width * 0.08,
          y: natural.height * 0.08,
          width: natural.width * 0.84,
          height: natural.height * 0.84,
        },
        natural.width,
        natural.height
      )
    );
    setCropping(true);
  };

  const applyCrop = async () => {
    if (!crop || busy) return;
    setBusy(true);
    try {
      onSrcChange(await cropImageDataUrl(src, crop));
    } finally {
      setBusy(false);
    }
  };

  const onPointerDown =
    (kind: Drag['kind']) => (event: PointerEvent<HTMLDivElement>) => {
      if (!crop) return;
      const pos = toNatural(event.clientX, event.clientY);
      if (!pos) return;
      event.preventDefault();
      event.stopPropagation();
      overlayRef.current?.setPointerCapture(event.pointerId);
      dragRef.current = {
        kind,
        startX: pos.x,
        startY: pos.y,
        orig: crop,
      };
    };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const pos = toNatural(event.clientX, event.clientY);
    if (!pos) return;
    setCrop(
      clampCrop(applyDrag(drag, pos.x, pos.y), natural.width, natural.height)
    );
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (overlayRef.current?.hasPointerCapture(event.pointerId)) {
      overlayRef.current.releasePointerCapture(event.pointerId);
    }
  };

  const scaleX = viewSize.width / (natural.width || 1);
  const scaleY = viewSize.height / (natural.height || 1);

  return (
    <div className={styles.root}>
      <div className={styles.stage}>
        <img
          ref={imgRef}
          src={src}
          alt={alt ?? t('photo', 'Photo')}
          className={styles.image}
          draggable={false}
          onLoad={(event) => {
            const img = event.currentTarget;
            setNatural({
              width: img.naturalWidth,
              height: img.naturalHeight,
            });
            setViewSize({
              width: img.clientWidth,
              height: img.clientHeight,
            });
          }}
        />
        {cropping && crop && (
          <div
            ref={overlayRef}
            className={styles.overlay}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div
              className={styles.crop}
              style={{
                left: crop.x * scaleX,
                top: crop.y * scaleY,
                width: crop.width * scaleX,
                height: crop.height * scaleY,
              }}
              onPointerDown={onPointerDown('move')}
            >
              {HANDLES.map((handle) => (
                <div
                  key={handle}
                  className={styles.handle}
                  style={{
                    ...handlePosition(handle),
                    cursor: handleCursor[handle],
                  }}
                  onPointerDown={onPointerDown(handle)}
                />
              ))}
            </div>
          </div>
        )}
        {busy && (
          <div className={styles.busy}>
            <Spinner />
          </div>
        )}
      </div>
      <div className={styles.toolbar}>
        <Button
          appearance="secondary"
          icon={<ArrowRotateCounterclockwise24Regular />}
          disabled={busy || cropping}
          onClick={() => void rotate(-90)}
        >
          {t('rotateLeft', 'Rotate left')}
        </Button>
        <Button
          appearance="secondary"
          icon={<ArrowRotateClockwise24Regular />}
          disabled={busy || cropping}
          onClick={() => void rotate(90)}
        >
          {t('rotateRight', 'Rotate right')}
        </Button>
        {cropping ? (
          <>
            <Button
              appearance="primary"
              disabled={busy}
              onClick={() => void applyCrop()}
            >
              {t('applyCrop', 'Apply crop')}
            </Button>
            <Button
              appearance="secondary"
              disabled={busy}
              onClick={() => {
                setCropping(false);
                setCrop(null);
              }}
            >
              {t('cancel', 'Cancel')}
            </Button>
          </>
        ) : (
          <Button
            appearance="secondary"
            icon={<Crop24Regular />}
            disabled={busy || !natural.width}
            onClick={startCrop}
          >
            {t('crop', 'Crop')}
          </Button>
        )}
      </div>
    </div>
  );
};
